import React, { ComponentProps } from 'react'
import { Link, useLocation } from 'react-router-dom'

import { Icon } from '@/shared/components/Icon/Icon'
import { IconDefinition } from '@fortawesome/pro-regular-svg-icons'
import { styled, Tooltip } from '@mui/material'

export type NavItemProps = Omit<ComponentProps<typeof StyledNavItem>, 'to' | 'onClick'> & {
  children: React.ReactNode
  href: string
  icon?: IconDefinition
  isNavOpen?: boolean
  onClick?: () => void
}

export const NavItem: React.FC<NavItemProps> = ({ href, icon, isNavOpen, onClick, children, ...props }) => {
  const location = useLocation()
  const locationPaths = location.pathname.split('/')
  const hrefPaths = href.split('/')
  const isEqual = locationPaths[0] === hrefPaths[0] && locationPaths[1] === hrefPaths[1]

  return (
    <Tooltip arrow title={isNavOpen ? '' : children} placement="right" >
      <StyledNavItem onClick={onClick} to={href} $active={isEqual} $isNavOpen={isNavOpen} {...props}>
        {icon && <StyledNavIcon icon={icon} />}
        {children}
      </StyledNavItem>
    </Tooltip>
  )
}

export const StyledNavIcon = styled(Icon)`
  font-size: ${(props) => props.theme.typography.pxToRem(16)};
  width: ${(props) => props.theme.typography.pxToRem(32)};
`

export const StyledNavItem = styled(Link, {
  shouldForwardProp: (prop) => prop !== '$active' && prop !== '$isNavOpen',
}) <{ $active?: boolean, $isNavOpen?: boolean }>`
  font-family: ${(props) => props.theme.typography.fontFamily};
  font-size: ${(props) => props.theme.typography.pxToRem(14)};
  letter-spacing: 0.03em;
  display: flex;
  align-items: center;
  height: ${(props) => props.theme.typography.pxToRem(32)};
  min-height: ${(props) => props.theme.typography.pxToRem(32)};
  text-decoration: none;
  color: ${(props) => props.theme.palette.custom.sidebar.textDimColor};
  border-radius: ${(props) => props.theme.typography.pxToRem(4)};
  margin: ${(props) => props.theme.typography.pxToRem(7)} ${(props) => props.theme.typography.pxToRem(12)};
  overflow: hidden;
  gap: ${(props) => props.theme.typography.pxToRem(8)};
  white-space: nowrap;
  transition: background-color 150ms ease-in-out, color 150ms ease-in-out;

  ${(props) => props.theme.breakpoints.up('sm')} {
    &:hover {
      background-color: rgba(255, 255, 255, 0.15);
      color: ${(props) => props.theme.palette.custom.sidebar.color};
    }
  }

  ${(props) => props.$active && `
    background-color: rgba(255, 255, 255, 0.15);
    color: ${props.theme.palette.custom.sidebar.color};
  `}
`
